// Phase 129 Plan 04 Task 1 — Handoff JWT helpers.
//
// Two invariants this file enforces:
//
// 1. **UserFacingError on mint failure (Blocker 4 — CLAUDE.md HARD RULE).**
//    `mintHandoffJwt` throws `UserFacingError` from @hookmyapp/observability
//    on any non-2xx response. Raw `throw new Error(...)` is rejected by
//    `@typescript-eslint/only-throw-error` + `no-restricted-syntax` (web/
//    ESLint config). The chat panel handler catches and falls back to a
//    plain signup URL so the user can still proceed.
//
// 2. **Per-environment app/auth base URLs.** Production app =
//    app.hookmyapp.com, production auth = api.hookmyapp.com. Local dev uses
//    the existing ngrok app tunnel (uninked-robbi-boughless), where Vite
//    proxies auth paths. VITE_APP_BASE_URL / VITE_AUTH_API_URL take priority
//    for tests + explicit overrides.
// Browser-safe sub-path import — the barrel `@hookmyapp/observability` re-exports
// init/node + trace/* which transitively pull `@sentry/node` (Node-only). Plan
// 02 Phase 123 introduced the `/errors` subpath specifically for browser use
// (frontend/src/contexts/error-context.tsx etc.). Web/ uses the same.
import { UserFacingError } from '@hookmyapp/observability/errors';

/**
 * Build the post-demo CTA URL with the handoff JWT in the ?handoff= query.
 *
 * Phase 130 §4c — destination is /sandbox (not /signup) so a single URL
 * handles both authed (claim immediately) and unauthed (WorkOS AuthKit
 * redirects through its hosted flow; the SPA's sessionStorage carries
 * the JWT through the round-trip and the claim runs on return).
 *
 *   - Production: https://app.hookmyapp.com/sandbox?handoff=<jwt>
 *   - Staging:    https://staging-app.hookmyapp.com/sandbox?handoff=<jwt>
 *   - Local dev:  https://uninked-robbi-boughless.ngrok-free.dev/sandbox?handoff=<jwt>
 *
 * Pass an empty `jwt` to mean "no handoff" — the chat panel calls this in
 * the UserFacingError fallback so the user can still proceed without a
 * token (lands on /sandbox unauthed → AuthKit signs them up → returns to
 * /sandbox).
 */
/**
 * Per-environment app base URL for user-facing app destinations.
 *
 *   - VITE_APP_BASE_URL override wins for tests + explicit overrides.
 *   - Local dev → ngrok app tunnel (uninked-robbi-boughless).
 *   - Production → app.hookmyapp.com.
 *   - Staging → staging-app.hookmyapp.com.
 */
export function appBaseUrl(): string {
  const explicit = import.meta.env.VITE_APP_BASE_URL;
  if (typeof explicit === 'string' && explicit.length > 0) return explicit;
  if (import.meta.env.VITE_IS_LOCAL_DEV === '1') {
    return 'https://uninked-robbi-boughless.ngrok-free.dev';
  }
  return import.meta.env.MODE === 'production'
    ? 'https://app.hookmyapp.com'
    : 'https://staging-app.hookmyapp.com';
}

/**
 * AuthKit must start on the same host that receives the WorkOS callback.
 * The backend sets `hma_login_state` as a host-only cookie, and the callback
 * validates that cookie against `state.n`. Pointing login at app.hookmyapp.com
 * while WorkOS returns to api.hookmyapp.com drops the cookie at callback time.
 */
export function authBaseUrl(): string {
  const explicit = import.meta.env.VITE_AUTH_API_URL;
  if (typeof explicit === 'string' && explicit.length > 0) return explicit;
  if (import.meta.env.VITE_IS_LOCAL_DEV === '1') return appBaseUrl();
  return import.meta.env.MODE === 'production'
    ? 'https://api.hookmyapp.com'
    : 'https://staging-api.hookmyapp.com';
}

export function buildHandoffUrl(jwt: string): string {
  const base = appBaseUrl();
  if (!jwt) return `${base}/sandbox`;
  return `${base}/sandbox?handoff=${encodeURIComponent(jwt)}`;
}

/**
 * Mint a handoff JWT by calling web-api `POST /demo/handoff` with the demo
 * session id. Returns the encoded JWT string.
 *
 * Endpoint signature (from Plan 03 task 2 — web-api/src/demo/demo-public.controller.ts):
 *   - body: { demoSessionId: string }
 *   - 200: { token: string }
 *   - 422: session unbound or unknown
 *
 * Throws UserFacingError on non-2xx (Blocker 4). Caller catches and falls
 * back to a plain signup URL.
 */
export async function mintHandoffJwt(demoSessionId: string): Promise<string> {
  const baseUrl = import.meta.env.VITE_WEB_API_URL ?? '';
  // credentials:'include' so the HttpOnly demo reclaim cookie flows along
  // with the handoff request. The handoff endpoint does not currently gate
  // on the cookie, but keeping cross-origin demo fetches uniform avoids a
  // surprise should we add cookie-gated endpoints to the demo surface
  // (Phase 129 Codex review hardening).
  const res = await fetch(`${baseUrl}/demo/handoff`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ demoSessionId }),
  });
  if (!res.ok) {
    throw new UserFacingError({
      message: `mint handoff jwt failed: ${res.status}`,
      code: 'HANDOFF_JWT_MINT_FAILED',
      details: { status: res.status },
    });
  }
  const body = (await res.json()) as { token: string };
  return body.token;
}
